import React, { useState, useRef } from 'react';
import styled from 'styled-components';

interface VideoCardProps {
  id: string;
  videoUrl: string;
  thumbnailUrl: string;
  title: string;
  creator: {
    id: string;
    name: string;
    avatar: string;
  };
  likes: number;
  comments: number;
  location?: string;
  tags?: string[];
  isLiked?: boolean;
  onLike: (id: string) => void;
  onComment: (id: string) => void;
  onShare: (id: string) => void;
  onSave: (id: string) => void;
}

const CardContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 12px;
  overflow: hidden;
  background-color: #000;
`;

const VideoElement = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ThumbnailImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
  opacity: 1;
  transition: opacity 0.3s ease;
  
  &.hidden {
    opacity: 0;
    z-index: -1;
  }
`;

const VideoOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.3) 50%, rgba(0, 0, 0, 0) 100%);
  z-index: 2;
  height: 60%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const VideoTitle = styled.h3`
  color: #fff;
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 8px 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

const VideoMetadata = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
`;

const CreatorAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid #fff;
`;

const MetadataItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  font-weight: 500;
  background-color: rgba(0, 0, 0, 0.5);
  padding: 4px 10px;
  border-radius: 16px;
  backdrop-filter: blur(4px);
  
  svg {
    width: 16px;
    height: 16px;
    fill: currentColor;
  }
`;

const VideoLocation = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  margin-bottom: 8px;
  font-weight: 500;
`;

const VideoTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
`;

const Tag = styled.span`
  background-color: rgba(0, 0, 0, 0.5);
  color: #fff;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  backdrop-filter: blur(4px);
`;

const ActionButtons = styled.div`
  position: absolute;
  right: 16px;
  bottom: 120px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  z-index: 3;
`;

const ActionButton = styled.button<{ $isActive?: boolean }>`
  background: transparent;
  border: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  color: ${props => props.$isActive ? '#9333EA' : '#fff'};
  
  svg {
    width: 28px;
    height: 28px;
    fill: ${props => props.$isActive ? '#9333EA' : '#fff'};
  }
`;

const ActionCount = styled.span`
  color: #fff;
  font-size: 12px;
  font-weight: 600;
`;

const PlayButton = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
  
  svg {
    width: 30px;
    height: 30px;
    fill: #fff;
  }
  
  &.hidden {
    display: none;
  }
`;

const AddToButton = styled.button`
  background-color: rgba(255, 0, 0, 0.8);
  border: none;
  border-radius: 20px;
  color: white;
  font-size: 14px;
  padding: 6px 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  margin-top: 12px;
  font-weight: 500;
  
  svg {
    width: 16px;
    height: 16px;
    fill: currentColor;
  }
  
  &:hover {
    background-color: rgba(255, 0, 0, 1);
  }
`;

const VideoCard: React.FC<VideoCardProps> = ({
  id,
  videoUrl,
  thumbnailUrl,
  title,
  creator,
  likes,
  comments,
  location,
  tags,
  isLiked = false,
  onLike,
  onComment,
  onShare,
  onSave
}) => {
  const [playing, setPlaying] = useState(false);
  const [liked, setLiked] = useState(isLiked);
  const [likeCount, setLikeCount] = useState(likes);
  const videoRef = useRef<HTMLVideoElement>(null);

  const togglePlay = () => {
    if (videoRef.current) {
      if (playing) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setPlaying(!playing);
    }
  };

  const handleLike = () => {
    setLiked(!liked);
    setLikeCount(liked ? likeCount - 1 : likeCount + 1);
    onLike(id);
  };

  return (
    <CardContainer>
      <ThumbnailImage 
        src={thumbnailUrl} 
        alt={title} 
        className={playing ? 'hidden' : ''}
      />
      <VideoElement 
        ref={videoRef}
        src={videoUrl} 
        loop
        playsInline
        onClick={togglePlay}
      />
      <PlayButton 
        onClick={togglePlay}
        className={playing ? 'hidden' : ''}
      >
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M8 5v14l11-7z" />
        </svg>
      </PlayButton>
      
      <VideoOverlay>
        <VideoTitle>{title}</VideoTitle>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <CreatorAvatar src={creator.avatar} alt={creator.name} />
          <span style={{ color: '#fff', fontSize: '14px', fontWeight: 500 }}>{creator.name}</span>
        </div>
        
        {location && (
          <VideoLocation>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="currentColor"/>
            </svg>
            {location}
          </VideoLocation>
        )}
        
        {tags && tags.length > 0 && (
          <VideoTags>
            {tags.map((tag, index) => (
              <Tag key={index}>#{tag}</Tag>
            ))}
          </VideoTags>
        )}
        
        <AddToButton>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
          </svg>
          Add It To Your Bucketlist
        </AddToButton>
        
        <VideoMetadata>
          <MetadataItem style={{ backgroundColor: '#1976D2' }}>
            <span>B</span>
          </MetadataItem>
          <MetadataItem>
            <span>90/100</span>
          </MetadataItem>
          <MetadataItem>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2Z" fill="currentColor"/>
            </svg>
            <span>Turkey</span>
          </MetadataItem>
        </VideoMetadata>
      </VideoOverlay>
      
      <ActionButtons>
        <ActionButton $isActive={liked} onClick={handleLike}>
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            {liked ? (
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            ) : (
              <path d="M16.5 3c-1.74 0-3.41.81-4.5 2.09C10.91 3.81 9.24 3 7.5 3 4.42 3 2 5.42 2 8.5c0 3.78 3.4 6.86 8.55 11.54L12 21.35l1.45-1.32C18.6 15.36 22 12.28 22 8.5 22 5.42 19.58 3 16.5 3zm-4.4 15.55l-.1.1-.1-.1C7.14 14.24 4 11.39 4 8.5 4 6.5 5.5 5 7.5 5c1.54 0 3.04.99 3.57 2.36h1.87C13.46 5.99 14.96 5 16.5 5c2 0 3.5 1.5 3.5 3.5 0 2.89-3.14 5.74-7.9 10.05z" />
            )}
          </svg>
          <ActionCount>{likeCount}</ActionCount>
        </ActionButton>
        
        <ActionButton onClick={() => onComment(id)}>
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M21.99 4c0-1.1-.89-2-1.99-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4-.01-18zM18 14H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
          </svg>
          <ActionCount>{comments}</ActionCount>
        </ActionButton>
        
        <ActionButton onClick={() => onShare(id)}>
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z" />
          </svg>
          <ActionCount>Share</ActionCount>
        </ActionButton>
        
        <ActionButton onClick={() => onSave(id)}>
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M17 3H7c-1.1 0-1.99.9-1.99 2L5 21l7-3 7 3V5c0-1.1-.9-2-2-2zm0 15l-5-2.18L7 18V5h10v13z" />
          </svg>
          <ActionCount>Save</ActionCount>
        </ActionButton>
      </ActionButtons>
    </CardContainer>
  );
};

export default VideoCard;
